'use client';
import { Box, Button, RadioGroup, RadioIcon, Stack, Tag, Typography } from '@castlery/fortress';
import { useBreakpoints } from '@castlery/fortress/hooks';
import { Dimension, PlayCircle } from '@castlery/fortress/Icons';
import { Image, Product, Variant } from '@castlery/modules-product-domain';
import { FortressImage, FortressVideo } from '@castlery/shared-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { default as ReactPlayerType } from 'react-player';
import { BaseOverlayImage } from './base-overlay-image';
import ThreeSixtyViewImage from './three-sixty-view-image';
import { EVENT_PDP_DETAILS } from '@castlery/modules-tracking-services';
import { useAppDispatch } from '@castlery/shared-redux-store';
import { videosOptionTypes } from '@castlery/modules-product-services';

export interface MediaItemProps {
  media: Image;
  variant: Variant;
  isCurrent: boolean;

  product: Product;
  index: number;
  masterVideo?: Image;
  videoRefs?: React.MutableRefObject<Map<number, ReactPlayerType & { handleClickPreview?: () => void }>>;
  handleOnViewMoreFeatures: () => void;
  onDimensionToggle?: (isActive: boolean, index: number) => void;
  onVideoPlay?: () => void;
  onVideoPause?: () => void;
  galleryContainer?: Element | null;
  // onepiece 中该属性提供给外部 gallery 用来处理 react slick 的 swipe 状态，待定
  // popShowMasterVideoStatus?: (status: boolean) => void
}

export const MediaItem: React.FC<MediaItemProps> = ({
  media,
  variant,
  isCurrent: _isCurrent,
  product,
  index,
  masterVideo,
  videoRefs,
  handleOnViewMoreFeatures,
  onDimensionToggle,
  onVideoPlay,
  onVideoPause,
  galleryContainer,
  // popShowMasterVideoStatus,
}) => {
  const dispatch = useAppDispatch();
  const [isDimensionActive, setIsDimensionActive] = useState<boolean | undefined>(false);
  const [showDimension, setShowDimension] = useState<boolean>(false);
  const { desktop, mobile } = useBreakpoints();

  const targetLinks = useMemo(() => {
    return variant?.dimension_image?.links || product?.dimension_image?.links;
  }, [variant?.dimension_image?.links, product?.dimension_image?.links]);

  const { dimensionImage, dimensionGrayImage } = useMemo(() => {
    return {
      dimensionImage: targetLinks?.feed,
      dimensionGrayImage: targetLinks?.large_gray,
    };
  }, [targetLinks]);

  // useEffect(() => {
  //   if (popShowMasterVideoStatus) {
  //     popShowMasterVideoStatus(showMasterVideo);
  //   }
  // }, [showMasterVideo, popShowMasterVideoStatus]);
  const isBaseImage = useMemo(() => media.type === 'base' || media.type === 'base_old', [media.type]);

  useEffect(() => {
    if (isBaseImage && dimensionImage) {
      setShowDimension(true);
    } else {
      setShowDimension(false);
    }
  }, [isBaseImage, dimensionImage]);

  const handleTrackPDPDetails = useCallback(
    async ({ action }: { action: string }) => {
      await dispatch(
        EVENT_PDP_DETAILS({
          action,
        })
      );
    },
    [dispatch]
  );

  const handleViewDimension = useCallback(
    (e: React.MouseEvent, isActive: boolean) => {
      setIsDimensionActive(isActive);
      if (onDimensionToggle) {
        onDimensionToggle(isActive, index);
      }
      if (isActive) {
        handleTrackPDPDetails({ action: 'dimension_click' });
      }
    },
    [onDimensionToggle, index, handleTrackPDPDetails]
  );
  if (index === 0 && masterVideo) {
    return (
      <Stack
        direction={'row'}
        alignItems={'center'}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          pointerEvents: 'auto',
        }}
      >
        <FortressImage
          src={media.links?.feed || ''}
          alt={`${product.name} image ${index}`}
          lazy={false}
          objectFit={mobile && !isBaseImage ? 'cover' : 'contain'}
          sx={{
            ...(mobile &&
              !isBaseImage && {
                height: '100%',
                '--AspectRatio-paddingBottom': 0,
              }),
          }}
          sizes={['0.65-md', '1-sm', '1-xs']}
        />
        {variant?.badges?.[0] && mobile && (
          <Tag
            variant="solid"
            sx={{
              position: 'absolute',
              top: (theme: any) => theme.spacing(6),
              left: (theme: any) => theme.spacing(6),
            }}
          >
            <Typography level="caption2">{variant?.badges[0]}</Typography>
          </Tag>
        )}
        <Box
          sx={{
            position: 'absolute',
            right: (theme: any) => theme.spacing(6),
            ...(variant?.badges?.[0] && mobile
              ? {
                  bottom: (theme: any) => theme.spacing(6),
                }
              : {
                  top: (theme: any) => theme.spacing(6),
                }),
          }}
        >
          <Button imageButtonModule variant="solid" startDecorator={<PlayCircle />} onClick={handleOnViewMoreFeatures}>
            {desktop ? 'View more features' : 'more features'}
          </Button>
        </Box>
      </Stack>
    );
  }

  if (media.type === 'short_video') {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <FortressVideo
          ref={(ref) => {
            if (videoRefs && ref) {
              videoRefs.current.set(index, ref);
            }
          }}
          src={media?.path || ''}
          autoPlay
          loop
          muted
          onPlay={onVideoPlay}
          onPause={onVideoPause}
          rootContainer={galleryContainer}
          threshold={0.6}
          autoPauseOnVisible={true}
          autoPlayOnVisible={true}
          videoConfig={{
            ac: true,
          }}
          controls={false}
          sx={{
            ...(mobile && {
              height: '100%',
              pb: 0,
            }),
          }}
        />
      </Box>
    );
  }

  if (videosOptionTypes.includes(media.type)) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <FortressVideo
          ref={(ref) => {
            if (videoRefs && ref) {
              videoRefs.current.set(index, ref);
            }
          }}
          src={media?.path || ''}
          onPlay={onVideoPlay}
          onPause={onVideoPause}
          rootContainer={galleryContainer}
          muted
          threshold={0.6}
          autoPauseOnVisible={true}
          autoPlayOnVisible={true}
          thumbnailConfig={{
            // thumbnailWidth: desktop ? 1500 : 600,
            thumbnailQuality: 'best',
          }}
          sx={{
            ...(mobile && {
              height: '100%',
              pb: 0,
            }),
          }}
        />
      </Box>
    );
  }

  // 360度视图处理
  if (media.type === '3d') {
    return <ThreeSixtyViewImage variant={variant} position={index} />;
  }

  if (media?.links) {
    return (
      <Stack
        direction={'row'}
        alignItems={'center'}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        {isBaseImage ? (
          <BaseOverlayImage
            src={isDimensionActive ? dimensionImage || dimensionGrayImage : media.links?.feed || ''}
            alt={`${product.name} ${index}`}
            bundleOptions={product?.bundle_options}
            overlayImageSizes={['0.7-md', '1-sm', '1-xs']}
            lazy={index >= 1}
            {...{
              sizes: ['0.7-md', '1-sm', '1-xs'],
            }}
          />
        ) : (
          <FortressImage
            src={isDimensionActive ? dimensionImage || dimensionGrayImage : media.links?.feed || ''}
            alt={`${product.name} ${index}`}
            lazy={index >= 1}
            objectFit={mobile ? 'cover' : 'contain'}
            sx={{
              ...(mobile && {
                height: '100%',
                '--AspectRatio-paddingBottom': 0,
              }),
            }}
            sizes={['0.7-md', '1-sm', '1-xs']}
          />
        )}
        {variant?.badges?.[0] && mobile && !masterVideo && index === 0 && (
          <Tag
            variant="solid"
            sx={{
              position: 'absolute',
              top: (theme: any) => theme.spacing(6),
              left: (theme: any) => theme.spacing(6),
            }}
          >
            <Typography level="caption2">{variant?.badges[0]}</Typography>
          </Tag>
        )}
        {showDimension && (
          <RadioGroup
            value={isDimensionActive ? 'showDimensionImage' : ''}
            name="product-dimension"
            orientation="horizontal"
            aria-label="Show product dimensions"
            title="Show product dimensions"
            sx={(theme) => ({
              position: 'absolute',
              top: theme.spacing(8),
              right: theme.spacing(8),
              '.Mui-checked': {
                svg: {
                  path: {
                    fill: 'var(--fortress-palette-brand-warmLinen-500) !important',
                  },
                },
              },
              svg: {
                width: mobile ? '24px' : '44px',
                height: mobile ? '24px' : '44px',
              },
              '.MuiRadio-root': {
                '--Radio-size': mobile ? '40px' : '60px',
              },
            })}
            onClick={(e: React.MouseEvent) => handleViewDimension(e, !isDimensionActive)}
          >
            <RadioIcon
              data-show-dimension-icon={showDimension}
              value="showDimensionImage"
              variant="outlined"
              overlay
              uncheckedIcon={<Dimension />}
              checkedIcon={<Dimension />}
              slotProps={{
                input: {
                  'aria-label': 'Toggle product dimensions',
                  title: 'Toggle product dimensions',
                },
              }}
            />
          </RadioGroup>
        )}
      </Stack>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <FortressImage
        src={''}
        alt={`${product.name} ${index}`}
        ratio={1}
        objectFit={mobile ? 'cover' : 'contain'}
        sx={{
          ...(mobile && {
            height: '100%',
            '--AspectRatio-paddingBottom': 0,
          }),
        }}
        sizes={['0.7-md', '1-sm', '1-xs']}
      />
    </Box>
  );
};
